import {
  isDigitalWellBeingPath,
  getDigitalWellBeingProgramType,
  matchesHighSchoolPath,
  matchesMiddleSchoolTier2Path,
  matchesMiddleSchoolPath,
  matchesElementaryTier2Path,
  matchesElementaryPath,
  getPathname,
} from '../routing/routeHelpers'

export const PROGRAM_TYPES = {
  HIGH_SCHOOL: 'HIGH_SCHOOL',
  MIDDLE_SCHOOL: 'MIDDLE_SCHOOL',
  MIDDLE_SCHOOL_TIER_2: 'MIDDLE_SCHOOL_TIER_2',
  ELEMENTARY: 'ELEMENTARY',
  ELEMENTARY_TIER_2: 'ELEMENTARY_TIER_2',
  DWB: 'DWB',
  UNKNOWN: 'UNKNOWN',
}

/**
 * Determines the current program type based on the URL pathname
 * @returns {string} The program type from PROGRAM_TYPES enum
 */
export const getCurrentProgram = () => {
  const pathname = getPathname()

  if (!pathname) {
    return PROGRAM_TYPES.UNKNOWN
  }

  if (isDigitalWellBeingPath(pathname)) {
    const band = getDigitalWellBeingProgramType(pathname)
    if (band === 'k5') return PROGRAM_TYPES.ELEMENTARY
    if (band === 'highSchool') return PROGRAM_TYPES.HIGH_SCHOOL
    return PROGRAM_TYPES.MIDDLE_SCHOOL
  }

  if (matchesHighSchoolPath(pathname)) return PROGRAM_TYPES.HIGH_SCHOOL

  if (matchesMiddleSchoolTier2Path(pathname))
    return PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2

  if (matchesMiddleSchoolPath(pathname)) return PROGRAM_TYPES.MIDDLE_SCHOOL

  if (matchesElementaryTier2Path(pathname))
    return PROGRAM_TYPES.ELEMENTARY_TIER_2

  if (matchesElementaryPath(pathname)) return PROGRAM_TYPES.ELEMENTARY

  return PROGRAM_TYPES.UNKNOWN
}

export const isHighSchoolProgram = () =>
  getCurrentProgram() === PROGRAM_TYPES.HIGH_SCHOOL

export const isMiddleSchoolProgram = () =>
  getCurrentProgram() === PROGRAM_TYPES.MIDDLE_SCHOOL

export const isElementaryProgram = () =>
  getCurrentProgram() === PROGRAM_TYPES.ELEMENTARY

export const isMiddleSchoolTier2Program = () =>
  getCurrentProgram() === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2

export const isElementaryTier2Program = () =>
  getCurrentProgram() === PROGRAM_TYPES.ELEMENTARY_TIER_2

/**
 * Extracts the specific grade from a single-grade lesson URL, if present.
 *
 * Single-grade lessons live under `lessons/(preview|teach)/grade-:grade`
 * (e.g. `grade-k`, `grade-3`, `grade-12`). Tier-2 routes instead use
 * `lessons/tier-2/:verb/:gradeBand` where the band is a range such as
 * `grades-k-2`, `grades-3-5`, or `grades-6-8`. Anchoring to the
 * preview/teach grade segment (and rejecting a trailing range delimiter)
 * ensures a tier-2 band is never mistaken for a single grade.
 *
 * Only the supported grades are matched: `k` or `1`–`12` (no leading
 * zeros), so values like `grade-0`, `grade-03`, or `grade-99` are ignored.
 *
 * @param {string} pathname - The URL pathname to inspect
 * @returns {string|null} The grade identifier (e.g. 'k', '3', '12') or null when not found
 */
export const getGradeFromPath = (pathname = '') => {
  if (typeof pathname !== 'string') {
    return null
  }

  const match = pathname.match(
    /lessons\/(?:preview|teach)\/grade-(k|1[0-2]|[1-9])(?![\dk-])/i,
  )
  return match ? match[1].toLowerCase() : null
}

/**
 * Gets the appropriate theme name for the current program.
 * When the URL targets a specific grade (e.g. `grade-3`), a grade-specific
 * theme is returned (e.g. `grade3`). Otherwise the program band theme is used.
 * @returns {string} Theme name for the current program
 */
export const getProgramTheme = () => {
  const pathname = getPathname()

  if (isDigitalWellBeingPath(pathname)) {
    return 'gradems'
  }

  const program = getCurrentProgram()
  const grade = getGradeFromPath(pathname)

  switch (program) {
    case PROGRAM_TYPES.ELEMENTARY:
    case PROGRAM_TYPES.ELEMENTARY_TIER_2:
      return grade ? `grade${grade}` : 'gradek5'
    case PROGRAM_TYPES.MIDDLE_SCHOOL:
    case PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2:
      return grade ? `grade${grade}` : 'gradems'
    case PROGRAM_TYPES.HIGH_SCHOOL:
      return grade ? `grade${grade}` : 'gradehs'
    default:
      return 'gradehs'
  }
}

export const ELEMENTARY_URL = '/product/elementary/'
export const MIDDLE_SCHOOL_URL = '/product/middle-school/'
export const ELEMENTARY_INDIVIDUAL_RESOURCE_PATH = `${ELEMENTARY_URL}lessons/tier-2`
export const MIDDLE_SCHOOL_INDIVIDUAL_RESOURCE_PATH = `${MIDDLE_SCHOOL_URL}lessons/tier-2`
export const viableProgramTypesForTieredInterventions = [
  'elementary',
  'middle-school',
]
